import type React from 'react';

import { Badge, FocusRing, Image, Text } from '@change/ds-core-ui';
import { PlayArrowIcon } from '@change/ds-icons';

import { TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import type { VideoStory } from '#src/app/pages/about/impact/pageContext';
import { useFocusFallbackRef } from '#src/app/shared/hooks/focus';

import { withAutoplayParam } from './utils/withAutoplayParam';

type Props = Readonly<{
	story: VideoStory;
	isActive: boolean;
	isPlaying: boolean;
	onPlay: () => void;
}>;

/**
 * Uses a facade: the embed iframe only mounts once the slide is played, so no third-party player loads
 * with the page. Unmounting it again is what stops playback.
 */
export const VideoSlide: React.FC<Props> = ({ story, isPlaying, onPlay }) => {
	const { translate } = useI18n();
	// keeps focus inside the frame when the play button unmounts
	const frameRef = useFocusFallbackRef();

	return (
		<div className="flex flex-col gap-4">
			<div
				ref={frameRef}
				className="bg-typography-strong-fixed relative aspect-video overflow-hidden rounded-2xl"
				data-qa="impact-video-slide-frame"
			>
				{isPlaying ? (
					<iframe
						src={withAutoplayParam(story.videoUrl)}
						title={story.title}
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
						referrerPolicy="strict-origin-when-cross-origin"
						allowFullScreen
						className="absolute inset-0 h-full w-full"
					/>
				) : (
					<>
						{/* Empty alt: the play button covering the poster already carries the name. */}
						<Image
							aspectRatio="16:9"
							alt=""
							className="absolute inset-0 h-full w-full object-cover"
							src={story.posterUrl}
						/>
						<FocusRing focusRingClass="rounded-2xl">
							<button
								type="button"
								aria-label={translate('corgi.impact_page.petition_starters.play_video_label', { title: story.title })}
								onClick={(event) => {
									// the slide wrapper's own click would otherwise also select the slide
									event.stopPropagation();
									onPlay();
								}}
								className="absolute inset-0 flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
							>
								<span className="text-icon-inverse-strong-fixed flex aspect-square w-[11%] items-center justify-center rounded-full bg-black">
									<PlayArrowIcon size={32} />
								</span>
							</button>
						</FocusRing>
					</>
				)}
			</div>
			<div className="flex flex-col items-start gap-2">
				<Badge
					variant="information"
					emphasis="strong"
					size="medium"
					label={
						<TranslatePlural
							value="corgi.impact_page.petition_starters.signature_count"
							count={story.signatureCount}
							replacements={{ countLocalized: story.signatureCountLocalized }}
						/>
					}
				/>
				<Text as="h3" variant="heading" size="small" fontWeight="bold">
					{story.title}
				</Text>
			</div>
		</div>
	);
};
